import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Leaf, Search, Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getFileUrl, getBackendURL, getImageUrl } from '../utils/assetUrlHelper';

// API_URL was here




// Variety Card Component - defined outside main component
const VarietyCard = React.memo(({ variety, isPrioritized, onSelect }) => (
  <div
    onClick={() => onSelect(variety)}
    className={`bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 group cursor-pointer ${isPrioritized
      ? 'border-amber-300 ring-2 ring-amber-100 shadow-md'
      : 'border-gray-200'
      }`}
    data-testid={`variety-card-${variety.id}`}
  >
    <div className="relative">
      <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
        <img
          src={getImageUrl(variety.image_url)}
          alt={variety.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          onError={(e) => { e.target.src = '/images/ambika.jpg'; }}
        />
      </div>

      {/* Priority Badge */}
      {isPrioritized && (
        <div className="absolute top-2 left-2">
          <Badge className="bg-amber-500 text-white gap-1">
            <Star className="w-3 h-3 fill-current" /> Featured
          </Badge>
        </div>
      )}

      {/* Category Badges */}
      <div className="absolute top-2 right-2 flex flex-col gap-1">
        {variety.is_nutraceutical && (
          <Badge className="bg-purple-500 text-white text-[10px]">
            Nutraceutical
          </Badge>
        )}
        {variety.is_climate_resilient && (
          <Badge className="bg-blue-500 text-white text-[10px]">
            Climate Resilient
          </Badge>
        )}
      </div>
    </div>

    <div className="p-4">
      <h3 className="font-semibold text-base text-gray-800 mb-1 group-hover:text-[#0E6D2B] transition-colors">
        {variety.name}
      </h3>

      {variety.fruit_type && (
        <span className="inline-block px-2 py-0.5 bg-[#F4F8EF] text-[#0E6D2B] text-xs rounded-full mb-2">
          {variety.fruit_type}
        </span>
      )}

      {variety.summary && (
        <p className="text-gray-500 text-sm line-clamp-2">
          {variety.summary.replace(/<[^>]*>/g, '')}
        </p>
      )}

      {variety.year_of_release && (
        <p className="text-xs text-gray-400 mt-2">
          Released: {variety.year_of_release}
        </p>
      )}
    </div>
  </div>
));

VarietyCard.displayName = 'VarietyCard';

export default function VarietiesPage() {
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedVariety, setSelectedVariety] = useState(null);

  useEffect(() => {
    const fetchVarieties = async () => {
      try {
        const backendUrl = getBackendURL();
        const response = await fetch(`${backendUrl}/api/varieties`);
        if (response.ok) {
          const data = await response.json();
          setVarieties(data);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVarieties();
  }, []);

  const handleSelectVariety = useCallback((variety) => {
    setSelectedVariety(variety);
  }, []);

  const filteredVarieties = varieties.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || v.fruit_type?.toLowerCase() === filter;
    return matchesSearch && matchesFilter;
  });

  // Separate prioritized (priority > 0) and normal varieties
  const prioritizedVarieties = filteredVarieties
    .filter(v => v.priority && v.priority > 0)
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));

  const normalVarieties = filteredVarieties
    .filter(v => !v.priority || v.priority === 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main id="main-content" className="py-8 md:py-12">
        <div className="container-cish">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <Leaf className="w-6 h-6 text-[#0E6D2B]" />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0E6D2B]">
                Varieties for Commercialization
              </h1>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              Explore our released fruit varieties developed through years of research
            </p>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search varieties..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white"
                data-testid="variety-search"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E6D2B] bg-white"
              data-testid="variety-filter"
            >
              <option value="all">All Fruits</option>
              <option value="mango">Mango</option>
              <option value="guava">Guava</option>
              <option value="bael">Bael</option>
              <option value="jamun">Jamun</option>
              <option value="banana">Banana</option>
              <option value="aonla">Aonla</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-[#0E6D2B] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <>
              {/* Featured/Prioritized Varieties Section */}
              {prioritizedVarieties.length > 0 && (
                <section className="mb-12">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-amber-500" />
                    <h2 className="text-xl font-bold text-gray-800">Featured Varieties</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {prioritizedVarieties.map((variety, index) => (
                      <VarietyCard
                        key={variety.id || index}
                        variety={variety}
                        isPrioritized={true}
                        onSelect={handleSelectVariety}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* All Varieties Section */}
              {normalVarieties.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <Leaf className="w-5 h-5 text-[#0E6D2B]" />
                    <h2 className="text-xl font-bold text-gray-800">All Varieties</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                    {normalVarieties.map((variety, index) => (
                      <VarietyCard
                        key={variety.id || index}
                        variety={variety}
                        isPrioritized={false}
                        onSelect={handleSelectVariety}
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {filteredVarieties.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              No varieties found matching your search.
            </div>
          )}
        </div>
      </main>

      {/* Variety Detail Modal */}
      <Dialog open={!!selectedVariety} onOpenChange={() => setSelectedVariety(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedVariety && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl text-[#0E6D2B] flex items-center gap-2">
                  {selectedVariety.name}
                  {selectedVariety.priority > 0 && (
                    <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Image */}
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden max-w-md mx-auto">
                  <img
                    src={getImageUrl(selectedVariety.image_url)}
                    alt={selectedVariety.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = '/images/ambika.jpg'; }}
                  />
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {selectedVariety.fruit_type && (
                    <Badge variant="outline" className="border-[#0E6D2B] text-[#0E6D2B]">
                      {selectedVariety.fruit_type}
                    </Badge>
                  )}
                  {selectedVariety.is_nutraceutical && (
                    <Badge className="bg-purple-500">Nutraceutical Rich</Badge>
                  )}
                  {selectedVariety.is_climate_resilient && (
                    <Badge className="bg-blue-500">Climate Resilient</Badge>
                  )}
                </div>

                {/* Summary */}
                {selectedVariety.summary && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Summary</h4>
                    <p className="text-gray-600 text-sm">{selectedVariety.summary.replace(/<[^>]*>/g, '')}</p>
                  </div>
                )}

                {/* Description */}
                {selectedVariety.description && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Description</h4>
                    <div
                      className="text-gray-600 text-sm prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedVariety.description }}
                    />
                  </div>
                )}

                {/* Registration & Identification */}
                {(selectedVariety.ic_number || selectedVariety.ppvfra_registration) && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="font-semibold text-[#0E6D2B] mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#0E6D2B] rounded-full"></span>
                      Registration & Identification
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {selectedVariety.ic_number && (
                        <div>
                          <span className="font-medium text-gray-700">IC Number:</span>
                          <p className="text-gray-600">{selectedVariety.ic_number}</p>
                        </div>
                      )}
                      {selectedVariety.ppvfra_registration && (
                        <div>
                          <span className="font-medium text-gray-700">PPVFRA Breeder's Right Registration:</span>
                          <p className="text-gray-600">{selectedVariety.ppvfra_registration}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Developer Information */}
                {(selectedVariety.inventors || selectedVariety.collaborators || selectedVariety.maintainer_inventor) && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-700 rounded-full"></span>
                      Developer Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {selectedVariety.inventors && (
                        <div>
                          <span className="font-medium text-gray-700">Inventor(s)/Developer(s):</span>
                          <p className="text-gray-600">{selectedVariety.inventors}</p>
                        </div>
                      )}
                      {selectedVariety.collaborators && (
                        <div>
                          <span className="font-medium text-gray-700">Collaborator(s):</span>
                          <p className="text-gray-600">{selectedVariety.collaborators}</p>
                        </div>
                      )}
                      {selectedVariety.maintainer_inventor && (
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700">Maintainer Inventor:</span>
                          <p className="text-gray-600">{selectedVariety.maintainer_inventor}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Year Information */}
                {(selectedVariety.year_of_development || selectedVariety.year_of_release || selectedVariety.year_of_commercialization) && (
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <h4 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-700 rounded-full"></span>
                      Timeline
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      {selectedVariety.year_of_development && (
                        <div>
                          <span className="font-medium text-gray-700">Year of Development:</span>
                          <p className="text-gray-600">{selectedVariety.year_of_development}</p>
                        </div>
                      )}
                      {selectedVariety.year_of_release && (
                        <div>
                          <span className="font-medium text-gray-700">Year of Release:</span>
                          <p className="text-gray-600">{selectedVariety.year_of_release}</p>
                        </div>
                      )}
                      {selectedVariety.year_of_commercialization && (
                        <div>
                          <span className="font-medium text-gray-700">Year of Commercialization:</span>
                          <p className="text-gray-600">{selectedVariety.year_of_commercialization}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Licensing Information */}
                {(selectedVariety.nature_of_license || selectedVariety.licensing_territory || selectedVariety.license_fee || selectedVariety.duration || selectedVariety.target_customers || selectedVariety.royalty) && (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-semibold text-purple-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                      Licensing Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {selectedVariety.nature_of_license && (
                        <div>
                          <span className="font-medium text-gray-700">Nature of License:</span>
                          <p className="text-gray-600">{selectedVariety.nature_of_license}</p>
                        </div>
                      )}
                      {selectedVariety.licensing_territory && (
                        <div>
                          <span className="font-medium text-gray-700">Licensing Territory:</span>
                          <p className="text-gray-600">{selectedVariety.licensing_territory}</p>
                        </div>
                      )}
                      {selectedVariety.duration && (
                        <div>
                          <span className="font-medium text-gray-700">Duration:</span>
                          <p className="text-gray-600">{selectedVariety.duration}</p>
                        </div>
                      )}
                      {selectedVariety.license_fee && (
                        <div>
                          <span className="font-medium text-gray-700">License Fee:</span>
                          <p className="text-gray-600">{selectedVariety.license_fee}</p>
                        </div>
                      )}
                      {selectedVariety.target_customers && (
                        <div>
                          <span className="font-medium text-gray-700">Target Customers:</span>
                          <p className="text-gray-600">{selectedVariety.target_customers}</p>
                        </div>
                      )}
                      {selectedVariety.royalty && (
                        <div>
                          <span className="font-medium text-gray-700">Royalty:</span>
                          <p className="text-gray-600">{selectedVariety.royalty}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
